import { useQuery } from '@tanstack/react-query';
import { Link, useParams, useLocation } from 'wouter';
import { ArrowLeft, Play, Music2, FileText, User, Settings } from 'lucide-react';
import { Layout } from '../components/Layout';
import { api } from '../lib/api';

export function SetlistViewPage() {
  const params = useParams<{ bandId: string; setlistId: string }>();
  const bandId = Number(params.bandId);
  const setlistId = Number(params.setlistId);
  const [, navigate] = useLocation();

  const { data: band } = useQuery({
    queryKey: ['band', bandId],
    queryFn: () => api.bands.get(bandId),
  });

  const { data: setlist, isLoading } = useQuery({
    queryKey: ['setlist', setlistId],
    queryFn: () => api.setlists.get(setlistId),
  });

  const items = setlist?.items || [];

  const getChartLink = (item: any, index: number): string | null => {
    if (item.personalVariantId) {
      return `/editor/personal/${item.personalVariantId}?setlist=${setlistId}&pos=${index}`;
    }
    if (item.baseChartId) {
      return `/editor/base/${item.baseChartId}?setlist=${setlistId}&pos=${index}`;
    }
    return null;
  };

  const getSongFallbackLink = (item: any) => `/bands/${bandId}/songs/${item.songId}`;

  const firstChartIndex = items.findIndex((item: any) => item.personalVariantId || item.baseChartId);
  const firstSongLink = firstChartIndex >= 0 ? getChartLink(items[firstChartIndex], firstChartIndex) : null;

  if (isLoading) {
    return (
      <Layout bandId={bandId} bandName={band?.name} title="Setlist">
        <div className="text-center py-12 text-gray-400">Loading...</div>
      </Layout>
    );
  }

  if (!setlist) {
    return (
      <Layout bandId={bandId} bandName={band?.name} title="Setlist">
        <div className="text-center py-12 text-gray-400">Setlist not found</div>
      </Layout>
    );
  }

  return (
    <Layout bandId={bandId} bandName={band?.name} title={setlist.name}>
      <div className="p-4 md:p-6 max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <Link href={`/bands/${bandId}/setlists`}>
            <button className="p-2 hover:bg-surface-800 rounded-lg transition-colors">
              <ArrowLeft className="w-5 h-5 text-gray-400" />
            </button>
          </Link>
          <div className="flex-1 min-w-0">
            <h2 className="text-2xl font-bold tracking-tight">{setlist.name}</h2>
            {setlist.eventDate && (
              <p className="text-sm text-gray-400 mt-0.5">
                {new Date(setlist.eventDate).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
          </div>
          <Link href={`/bands/${bandId}/setlists/${setlistId}`}>
            <button className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200 bg-surface-800 hover:bg-surface-700 px-3 py-1.5 rounded-lg transition-colors">
              <Settings className="w-3.5 h-3.5" />
              Edit Setlist
            </button>
          </Link>
        </div>

        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">{items.length} song{items.length !== 1 ? 's' : ''}</p>
          {firstSongLink && (
            <button
              onClick={() => navigate(firstSongLink)}
              className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-4 rounded-lg transition-colors text-sm"
            >
              <Play className="w-4 h-4" />
              Open First Song
            </button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="text-center py-16">
            <Music2 className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400 mb-2">No songs in this setlist.</p>
            <Link href={`/bands/${bandId}/setlists/${setlistId}`}>
              <span className="text-sm text-primary-400 hover:text-primary-300 cursor-pointer">
                Go to setlist setup to add songs
              </span>
            </Link>
          </div>
        ) : (
          <div className="bg-surface-900 border border-surface-700 rounded-xl overflow-hidden">
            <div className="grid grid-cols-[3rem_1fr_5rem_4rem_6rem] gap-0 px-4 py-2.5 border-b border-surface-700 text-xs font-medium text-gray-500 uppercase tracking-wider">
              <span>#</span>
              <span>Song</span>
              <span className="text-center">BPM</span>
              <span className="text-center">Key</span>
              <span className="text-right">Version</span>
            </div>
            {items.map((item: any, idx: number) => {
              const hasChart = item.personalVariantId || item.baseChartId;
              const isPersonal = !!item.personalVariantId;
              return (
                <div
                  key={item.itemId}
                  onClick={() => {
                    const link = getChartLink(item, idx);
                    navigate(link || getSongFallbackLink(item));
                  }}
                  className="grid grid-cols-[3rem_1fr_5rem_4rem_6rem] gap-0 px-4 py-3 border-b border-surface-800 last:border-b-0 hover:bg-surface-800/60 cursor-pointer transition-colors items-center group"
                >
                  <span className="text-sm font-mono text-gray-500 group-hover:text-gray-300 transition-colors">
                    {idx + 1}
                  </span>
                  <div className="min-w-0 pr-3">
                    <span className="text-sm font-medium text-gray-200 group-hover:text-white transition-colors block truncate">
                      {item.songTitle}
                    </span>
                    {item.songArtist && (
                      <span className="text-xs text-gray-500 block truncate">{item.songArtist}</span>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 text-center tabular-nums">
                    {item.defaultTempo || '—'}
                  </span>
                  <span className="text-xs text-gray-400 text-center">
                    {item.defaultKey || '—'}
                  </span>
                  <div className="flex justify-end">
                    {hasChart ? (
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                        isPersonal
                          ? 'bg-accent-600/20 text-accent-400'
                          : 'bg-surface-700 text-gray-400'
                      }`}>
                        {isPersonal ? <User className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                        {isPersonal ? 'Mine' : 'Base'}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-600">No chart</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
